import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { describeTransaction, formatDate, formatDateTime } from './walletFormat';
import styles from './WalletHistory.module.css';

// Cinco linhas bastam para o "o que aconteceu por último"; o resto fica no "Ver tudo"
const PREVIEW_COUNT = 5;
// Sem período, a API devolve no máximo os últimos 100 movimentos
const API_LIMIT = 100;

const KIND = {
  purchase: { Icon: ShoppingBagOutlinedIcon, tone: 'purchase', title: 'Compra' },
  used: { Icon: CheckCircleOutlineRoundedIcon, tone: 'used', title: 'Sessão usada' },
  returned: { Icon: ReplayRoundedIcon, tone: 'returned', title: 'Sessão devolvida' },
  expired: { Icon: EventBusyOutlinedIcon, tone: 'expired', title: 'Vencido' },
  transfer: { Icon: SwapHorizRoundedIcon, tone: 'transfer', title: 'Transferência' },
  other: { Icon: ReceiptLongOutlinedIcon, tone: 'transfer', title: 'Movimento' },
};

function HistoryRow({ transaction }) {
  const { kind, quantity, negative } = describeTransaction(transaction);
  const { Icon, tone, title } = KIND[kind] || KIND.other;
  const description = transaction.description || transaction.package_name || transaction.procedure_name || title;
  const detail = [transaction.package_name, transaction.procedure_name]
    .filter((text) => text && text !== description)
    .join(' · ');
  const appointmentDate = formatDate(transaction.appointment_date, { withYear: false });

  return (
    <li className={styles.row}>
      <span className={`${styles.icon} ${styles[tone]}`} title={title}><Icon fontSize="small" /></span>
      <div className={styles.text}>
        <span className={styles.description}>{description}</span>
        <span className={styles.secondary}>
          <span title={formatDateTime(transaction.created_at)}>{formatDate(transaction.created_at)}</span>
          {detail && <span className={styles.dot}>·</span>}
          {detail && <span>{detail}</span>}
          {transaction.appointment_id && (
            <>
              <span className={styles.dot}>·</span>
              <Link className={styles.link} to={`/appointment/${transaction.appointment_id}`}>
                {appointmentDate ? `Atendimento de ${appointmentDate}` : 'Ver atendimento'}
              </Link>
            </>
          )}
        </span>
      </div>
      {quantity && (
        <span className={`${styles.quantity} ${negative ? styles.negative : styles.positive}`}>{quantity}</span>
      )}
    </li>
  );
}

/** Movimentações da carteira em linhas leves: cinco primeiras e "Ver tudo". */
function WalletHistory({ transactions }) {
  const [expanded, setExpanded] = useState(false);
  const total = transactions.length;
  const truncated = total >= API_LIMIT;
  const visible = expanded ? transactions : transactions.slice(0, PREVIEW_COUNT);

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <h4 className={styles.title}>Movimentações · {truncated ? `últimas ${API_LIMIT}` : total}</h4>
        <span className={styles.hint}>Compras, sessões usadas e devoluções</span>
      </header>

      {total === 0 ? (
        <p className={styles.empty}>Nenhum movimento ainda.</p>
      ) : (
        <ul className={styles.list}>
          {visible.map((transaction) => (
            <HistoryRow key={transaction.transaction_id} transaction={transaction} />
          ))}
        </ul>
      )}

      {total > PREVIEW_COUNT && (
        <footer className={styles.footer}>
          <button type="button" className={styles.more} onClick={() => setExpanded((value) => !value)}>
            {expanded ? 'Ver menos' : (truncated ? `Ver as últimas ${API_LIMIT}` : `Ver tudo (${total})`)}
          </button>
        </footer>
      )}
    </section>
  );
}

export default WalletHistory;
